import type { Faction, PieceDefinition, PieceType } from "./types";

const d = (type: PieceType, category: PieceDefinition["category"], maxCount: number,
  tech: number, cost: number, productionTime: number, speed: number, minRange: number,
  maxRange: number, asset: string, consumesEnergy = false, shield = false,
  notInForest = false, targets: PieceDefinition["targets"] = []): PieceDefinition => ({
  type, category, maxCount, tech, cost, productionTime, speed, minRange, maxRange,
  asset, consumesEnergy, shield, notInForest, targets,
});

// Values and the intentionally non-obvious building artwork mapping are copied from
// Android 0.17.14 GameModel.PieceType. The extracted building filenames do not match
// the labels printed on Rules_EN page 3; the Android mapping is the runtime authority.
export const PIECES: Record<PieceType, PieceDefinition> = {
  CONSTRUCTION_YARD: d("CONSTRUCTION_YARD", "BUILDING", 3, 1, 10, 3, 0, 0, 0, "building_construction_yard.png", false, true),
  POWER_PLANT: d("POWER_PLANT", "BUILDING", 3, 1, 3, 2, 0, 0, 0, "building_refinery.png"),
  REFINERY: d("REFINERY", "BUILDING", 2, 1, 5, 3, 0, 0, 0, "building_factory.png", true, true),
  BARRACKS: d("BARRACKS", "BUILDING", 2, 1, 2, 2, 0, 0, 0, "building_mg_tower.png", true),
  FACTORY: d("FACTORY", "BUILDING", 2, 1, 5, 3, 0, 0, 0, "building_power_plant.png", true, true),
  HELIPAD: d("HELIPAD", "BUILDING", 2, 4, 1, 1, 0, 0, 0, "building_helipad.png", true),
  UPLINK: d("UPLINK", "BUILDING", 1, 5, 10, 3, 0, 0, 0, "building_barracks.png", true, true),
  MG_TOWER: d("MG_TOWER", "BUILDING", 4, 1, 1, 1, 0, 1, 1, "building_uplink.png", false, false, false, ["INFANTRY", "WHEELED"]),
  RAK_TOWER: d("RAK_TOWER", "BUILDING", 3, 2, 2, 2, 0, 1, 2, "building_rak_tower.png", true, false, false, ["WHEELED", "ARMORED", "AIR", "BUILDING"]),
  MG_INFANTRY: d("MG_INFANTRY", "INFANTRY", 10, 1, 1, 1, 1, 1, 1, "unit_mg_infantry.png", false, false, false, ["INFANTRY", "WHEELED"]),
  RAK_INFANTRY: d("RAK_INFANTRY", "INFANTRY", 8, 1, 2, 1, 1, 1, 1, "unit_rak_infantry.png", false, false, false, ["WHEELED", "ARMORED", "AIR", "BUILDING"]),
  ING_INFANTRY: d("ING_INFANTRY", "INFANTRY", 6, 1, 2, 1, 1, 0, 0, "unit_ing_infantry.png"),
  MG_BUGGY: d("MG_BUGGY", "WHEELED", 4, 1, 2, 2, 3, 1, 1, "unit_mg_buggy.png", false, false, false, ["INFANTRY", "WHEELED"]),
  HARVESTER: d("HARVESTER", "ARMORED", 3, 1, 3, 2, 2, 0, 0, "unit_harvester.png"),
  TANK: d("TANK", "ARMORED", 4, 2, 3, 2, 2, 1, 1, "unit_tank.png", false, false, false, ["WHEELED", "ARMORED", "BUILDING"]),
  MCV: d("MCV", "ARMORED", 2, 2, 10, 3, 1, 0, 0, "unit_mcv.png", false, false, true),
  APC: d("APC", "ARMORED", 2, 2, 2, 2, 2, 0, 0, "unit_apc.png"),
  ARTILLERY: d("ARTILLERY", "ARMORED", 3, 3, 5, 3, 1, 3, 4, "unit_artillery.png", false, false, true, ["WHEELED", "ARMORED", "BUILDING"]),
  MOBILE_FORTRESS: d("MOBILE_FORTRESS", "ARMORED", 2, 5, 6, 3, 1, 1, 2, "unit_mobile_fortress.png", false, true, false, ["INFANTRY", "WHEELED", "ARMORED", "AIR", "BUILDING"]),
  DROPSHIP: d("DROPSHIP", "AIR", 2, 4, 3, 2, 4, 0, 0, "unit_dropship.png"),
  FIGHTER: d("FIGHTER", "AIR", 3, 4, 4, 2, 5, 1, 1, "unit_fighter.png", false, false, false, ["INFANTRY", "AIR"]),
  BOMBER: d("BOMBER", "AIR", 3, 4, 5, 3, 4, 1, 1, "unit_bomber.png", false, false, false, ["WHEELED", "ARMORED", "BUILDING"]),
};

export const FACTION_META: Record<Faction, { label: string; color: string; logo: string }> = {
  EFA: { label: "EFA", color: "#f3c33b", logo: "faction_efa.png" },
  REDCAT: { label: "RedCat", color: "#d64235", logo: "faction_redcat.png" },
  GREEN: { label: "KOBRA", color: "#42ad5d", logo: "faction_kobra.png" },
  BLUE: { label: "EST", color: "#9ca6ad", logo: "faction_est.png" },
};

export const FACTION_BADGES: Record<Faction, string> = {
  EFA: "adaptive-sidebar/efa.png",
  REDCAT: "adaptive-sidebar/red-cat.png",
  GREEN: "adaptive-sidebar/kobra.png",
  BLUE: "adaptive-sidebar/est.png",
};

export const assetUrl = (name: string): string => `${import.meta.env.BASE_URL}assets/${name}`;
export const isBuilding = (type: PieceType) => PIECES[type].category === "BUILDING";
export const isAir = (type: PieceType) => PIECES[type].category === "AIR";
export const isArmored = (type: PieceType) => PIECES[type].category === "ARMORED";
export const isCombatAircraft = (type: PieceType) => type === "FIGHTER" || type === "BOMBER";
export const isTransport = (type: PieceType) => type === "APC" || type === "DROPSHIP";
